<script setup lang="ts">
import { Document } from '@contentful/rich-text-types'

type AlertVariant = 'error' | 'success' | 'warning' | 'info' | 'default'

const props = defineProps({
  id: String,
  variant: { type: String as PropType<AlertVariant>, default: 'info' },
  iconHeight: { type: Number },
  iconWidth: { type: Number },
  closeButton: Boolean,
  content: Object as PropType<Document>,
  colorChange: Boolean,
})

const emit = defineEmits(['alertBarToggle'])

const alertVariant = computed(() => ({
  class: `alert-bar--${props.variant}`,
}))
</script>

<template>
  <div class="alert-bar" :class="alertVariant.class">
    <div class="alert-bar__message">
      <svg-icon
        class="alert-bar__icon"
        v-if="variant !== 'default'"
        :name="`mhj:${variant}`"
        :height="iconHeight"
        :width="iconWidth"
      />
      <slot><RichText class="alert-bar__message-text" :document="content" /></slot>
    </div>
    <button
      v-if="closeButton"
      class="alert-bar__dismiss"
      :aria-label="$t('image.alt_text.close')"
      @click="$emit('alertBarToggle', false)"
    >
      <svg-icon name="mhj:close" />
    </button>
  </div>
</template>

<style lang="postcss">
.alert-bar {
  display: flex;
  animation: 1s cubic-bezier(0, 0, 0.2, 1);
  transition: height 1s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: var(--space-50);

  &.alert-bar--error {
    background-color: var(--status-error-bg);
    border: 1px solid var(--status-error-border);
  }

  &.alert-bar--warning {
    background-color: var(--status-warning-bg);
    border: 1px solid var(--status-warning);
  }

  &.alert-bar--success {
    background-color: var(--status-success-bg);
    border: 1px solid var(--status-success);
  }

  &.alert-bar--info {
    background-color: var(--status-info-bg);
    border: 1px solid var(--status-info);
  }

  &.alert-bar--default {
    background-color: var(--status-default-bg);
  }

  .alert-bar__icon {
    align-self: start;
    margin-right: var(--space-30);
  }

  .alert-bar__message {
    align-items: center;
    display: flex;
    justify-content: flex-start;
    margin: var(--space-30);
    width: 100%;
  }

  .alert-bar__message-text {
    margin: auto var(--space-30);
    color: var(--text-primary);
  }

  .alert-bar__dismiss {
    cursor: pointer;
    margin: var(--space-10) var(--space-10) var(--space-10) auto;
  }

  .rich-text.rich-text p {
    font-size: var(--text-base);
    margin: var(--space-00);
  }
}
</style>
